import { formatDayKeyShort, formatHourKey } from '../../lib/format.js';
import type { UsageTrendRow } from '../../lib/types.js';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** A tenant-local key ("YYYY-MM-DD" or "YYYY-MM-DDTHH:00") as a comparable instant; zone-free. */
const keyTime = (key: string): number =>
  Date.parse(key.length > 10 ? `${key}:00Z` : `${key}T00:00:00Z`);

const timeKey = (t: number, hourly: boolean): string => {
  const iso = new Date(t).toISOString();
  return hourly ? `${iso.slice(0, 13)}:00` : iso.slice(0, 10);
};

/**
 * Every bucket from the first to the last, so a quiet stretch shows as zero instead of
 * vanishing from the axis. The API only returns buckets that had activity.
 */
export const fillTrend = (rows: UsageTrendRow[], hourly: boolean): UsageTrendRow[] => {
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (!first || !last) return [];
  const step = hourly ? HOUR_MS : DAY_MS;
  const byKey = new Map(rows.map((r) => [r.date, r]));
  const filled: UsageTrendRow[] = [];
  for (let t = keyTime(first.date); t <= keyTime(last.date); t += step) {
    const key = timeKey(t, hourly);
    filled.push(
      byKey.get(key) ?? {
        date: key,
        turns: 0,
        totalTokens: 0,
        activeUsers: 0,
        costPerAnswerUsd: null,
        cacheShare: null,
      },
    );
  }
  return filled;
};

/**
 * Axis labels, unique per bucket: the charts place points by label, so a repeated one ("12 PM"
 * yesterday and today) would land two points on one spot. A repeat gets its day in front.
 */
export const trendLabels = (rows: UsageTrendRow[]): string[] => {
  const short = rows.map((r) =>
    r.date.length > 10 ? formatHourKey(r.date) : formatDayKeyShort(r.date),
  );
  const seen = new Map<string, number>();
  for (const label of short) seen.set(label, (seen.get(label) ?? 0) + 1);
  return rows.map((r, i) => {
    const label = short[i] ?? r.date;
    if ((seen.get(label) ?? 0) < 2) return label;
    return r.date.length > 10
      ? `${formatDayKeyShort(r.date)} ${label}`
      : `${label}/${r.date.slice(2, 4)}`;
  });
};

/** The rows a chart plots, gaps filled, each with its unique axis label. */
// Return type inferred: an object type, which the charts' Record<string, unknown> rows accept.
export const labeledTrend = (rows: UsageTrendRow[], hourly: boolean) => {
  const filled = fillTrend(rows, hourly);
  const labels = trendLabels(filled);
  return filled.map((r, i) => ({ ...r, label: labels[i] ?? r.date }));
};
